import { BehaviorSubject } from 'rxjs'
import { action, computed, observable, runInAction } from 'mobx'
import { Id } from '@/infra/id'
import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { IamApi } from '@/features/iam/api/iam-api'
import type { UserStore } from '@/features/iam/users/UserStore'
import type { WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import type { WorkspaceMemberStore } from '@/features/iam/members/WorkspaceMemberStore'
import type { PreselectedWorkspaceProvider } from '@/features/iam/workspaces/PreselectedWorkspaceProvider'
import type { TokenInfo } from '@/features/iam/auth/token-info'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil, shareLatest, whileActive } from '@/utils/rx-util'
import { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { ABORTED, resolveOnAbort } from '@/utils/abort-util'

/**
 * Contains information about the current user and workspace.
 */
export default class Session {
  /**
   * The current user.
   * @private
   */
  @observable private accessor _currentUser: User | null = null

  /**
   * The current workspace member.
   * @private
   */
  @observable private accessor _currentMember: WorkspaceMember | null = null

  /**
   * The current workspace.
   * @private
   */
  @observable private accessor _currentWorkspace: Workspace | null = null

  /**
   * Whether the {@link preselectWorkspace} method has run to completion
   * at least once.
   *
   * @private
   */
  private _workspacePreselectionCompleted$ = new BehaviorSubject(false)

  constructor(
    private readonly _api: IamApi,
    private readonly _userStore: UserStore,
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _workspaceMemberStore: WorkspaceMemberStore,
    private readonly _preselectedWorkspaceProvider: PreselectedWorkspaceProvider,
  ) {}

  /**
   * Emits the current workspace, including `null` if none is selected.
   * Emission starts after the initial workspace preselection has been
   * completed, regardless of one having been preselected.
   */
  readonly currentWorkspace$ = toStream(
    () => this._currentWorkspace,
    true,
  ).pipe(whileActive(this._workspacePreselectionCompleted$), shareLatest())

  /**
   * Emits the current user once logged in.
   */
  readonly currentUser$ = toStream(() => this._currentUser).pipe(
    filterNonNil(),
    shareLatest(),
  )

  /**
   * Emits the current member once a workspace has been selected, or the user
   * has signed up.
   */
  readonly currentMember$ = toStream(() => this._currentMember).pipe(
    filterNonNil(),
    shareLatest(),
  )

  /**
   * Gets the current user.
   */
  @computed
  get currentUser() {
    return this._currentUser ?? User.none
  }

  /**
   * Gets the current workspace.
   */
  @computed
  get currentWorkspace() {
    return this._currentWorkspace ?? Workspace.none
  }

  /**
   * Gets whether the external customer feature is enabled for the current workspace.
   */
  @computed
  get externalCustomerEnabled() {
    if (this._currentWorkspace) {
      return this._currentWorkspace.config.externalCustomer.enabled
    }

    const preselected = this._preselectedWorkspaceProvider.read()
    return (
      preselected?.type === 'Url' &&
      preselected.workspaceResource.config.external_customers_config?.enabled
    )
  }

  /**
   * Gets whether the allow requesting magic link feature is enabled for the current workspace.
   */
  @computed
  get allowRequestingMagicLinks() {
    if (this._currentWorkspace) {
      return (
        this._currentWorkspace.config.externalCustomer.enabled &&
        this._currentWorkspace.config.externalCustomer.allowRequestingMagicLinks
      )
    }

    const preselected = this._preselectedWorkspaceProvider.read()
    return (
      preselected?.type === 'Url' &&
      preselected.workspaceResource.config.external_customers_config
        ?.allow_requesting_magic_links
    )
  }

  /**
   * Gets the current workspace member.
   */
  @computed
  get currentMember() {
    return this._currentMember ?? WorkspaceMember.none
  }

  /**
   * Fetches and selects the given workspace as the current one and checks for membership.
   */
  async selectWorkspaceById(workspaceId: Id, signal?: AbortSignal) {
    const workspace = await this._workspaceStore.fetchWorkspace(workspaceId)
    signal?.throwIfAborted()
    if (!workspace) {
      return
    }

    await this.selectWorkspace(workspace, signal)
  }

  /**
   * Selects the given workspace as the current one and checks for membership.
   */
  async selectWorkspace(workspace: Workspace, signal?: AbortSignal) {
    this._preselectedWorkspaceProvider.save(Id.assert(workspace.id))

    // Check if the user is a member of the workspace.
    const member = await this._workspaceMemberStore.fetchWorkspaceMember(
      workspace.id,
      this.currentUser.id,
    )

    signal?.throwIfAborted()

    this._setWorkspaceAndMember(workspace, member)
  }

  /**
   * Authenticates the session.
   *
   * @param tokenInfo
   * @param signal
   */
  async authenticate(tokenInfo: TokenInfo, signal?: AbortSignal) {
    const authResponse = await this._api.authentication.authenticate(
      tokenInfo.token,
      signal,
    )
    const user = await this._userStore.fetchUserUsingToken(
      authResponse.public_id,
      tokenInfo.token,
      signal,
    )
    if (!user) {
      throw new Error(
        `Authenticating user failed because the user was not found (public ID: ${authResponse.public_id}, auth ID: ${authResponse.auth_id})`,
      )
    }

    runInAction(() => {
      this._currentUser = user
      if (tokenInfo.mfaEnabled) {
        this._currentUser.markMultiFactorAuthEnabled()
      }
    })
  }

  /**
   * Attempts preselecting a workspace if we don't currently have one selected.
   */
  async preselectWorkspace(signal?: AbortSignal) {
    if (this._currentWorkspace) {
      return
    }

    const preselection = this._preselectedWorkspaceProvider.read()
    if (!preselection) {
      this._workspacePreselectionCompleted$.next(true)
      return
    }

    const resolved = await resolveOnAbort(
      Promise.all([
        this._workspaceStore.fetchPreselectedWorkspace(preselection),
        this._workspaceMemberStore.fetchWorkspaceMember(
          preselection.workspaceId,
          this.currentUser.id,
        ),
      ]),
      signal,
    )

    if (resolved === ABORTED) {
      // Aborted.
      return
    }

    const [preselected, member] = resolved
    if (!preselected) {
      return
    }

    this._setWorkspaceAndMember(preselected, member)
    this._workspacePreselectionCompleted$.next(true)
  }

  /**
   * Sets the current workspace member.
   *
   * @param member
   */
  @action.bound
  setMember(member: WorkspaceMember) {
    this._currentMember = member
  }

  /**
   * Sets the workspace and member if we have one.
   *
   * @param workspace
   * @param member
   * @private
   */
  @action.bound
  private _setWorkspaceAndMember(
    workspace: Workspace,
    member: WorkspaceMember | null,
  ) {
    if (member) {
      this._currentMember = member
    }
    this._currentWorkspace = workspace
  }
}
